/**
 * Landmark Pillows homepage map
 * Uses Leaflet 1.9.4 (BSD-2-Clause) and Leaflet.markercluster 1.5.3 (MIT), vendored in theme/assets.
 * Natural Earth GeoJSON: land, admin boundaries, rivers/lakes (public domain).
 */
(function () {
  const LAND_COLOR = '#170f41';
  const COAST_STROKE = 'rgba(229, 218, 180, 0.55)';
  const BORDER_STROKE = 'rgba(229, 218, 180, 0.4)';
  const RIVER_STROKE = 'rgba(229, 218, 180, 0.5)';
  const WORLD_BOUNDS = [
    [-60, -180],
    [84, 180],
  ];
  const DETAIL_ZOOM = 5;
  const RIVER_ZOOM = 6;
  const CITY_MIN_ZOOM = 4;

  function maxCityRankForZoom(z) {
    if (z >= 12) return 4;
    if (z >= 9) return 3;
    if (z >= 7) return 2;
    if (z >= 5) return 1;
    if (z >= CITY_MIN_ZOOM) return 0;
    return -1;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async function fetchJson(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to load ${url}`);
    return response.json();
  }

  async function fetchAllMapProducts(dataBaseUrl) {
    const products = [];
    let page = 1;
    let pages = 1;

    do {
      const separator = dataBaseUrl.includes('?') ? '&' : '?';
      const url = `${dataBaseUrl}${separator}page=${page}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Map data request failed (${response.status})`);
      }
      const payload = await response.json();
      pages = Number(payload.pages) || 1;
      if (Array.isArray(payload.products)) {
        products.push(...payload.products);
      }
      page += 1;
    } while (page <= pages);

    return products;
  }

  function landStyle() {
    return {
      color: COAST_STROKE,
      fillColor: LAND_COLOR,
      fillOpacity: 1,
      weight: 0.85,
      opacity: 0.95,
    };
  }

  function boundaryStyle(map) {
    const z = map.getZoom();
    return {
      color: BORDER_STROKE,
      weight: z >= 10 ? 0.75 : z >= 6 ? 0.55 : 0.4,
      opacity: z >= 6 ? 0.85 : 0.55,
      fill: false,
    };
  }

  function riverStyle(map) {
    const z = map.getZoom();
    return {
      color: RIVER_STROKE,
      weight: z >= 10 ? 1.1 : z >= 8 ? 0.9 : 0.65,
      opacity: 0.9,
      fill: false,
    };
  }

  function createCityLabelIcon(name, zoom) {
    const sizeClass = zoom >= 9 ? 'landmark-map__city-label-text--lg' : '';
    return L.divIcon({
      className: 'landmark-map__city-label',
      html: `<span class="landmark-map__city-label-text ${sizeClass}">${escapeHtml(name)}</span>`,
      iconSize: [0, 0],
      iconAnchor: [0, 4],
    });
  }

  function createPinIcon(product) {
    const title = escapeHtml(product.title);
    const url = escapeHtml(product.url);
    const image = escapeHtml(product.image);

    return L.divIcon({
      className: 'landmark-map__pin',
      html: `<a href="${url}" class="landmark-map__pin-link" aria-label="${title}"><img src="${image}" alt="" width="48" height="48" loading="lazy" decoding="async"></a>`,
      iconSize: [48, 48],
      iconAnchor: [24, 24],
    });
  }

  function initMap(container) {
    const landOverviewUrl = container.dataset.geoJsonUrl;
    const landDetailUrl = container.dataset.geoJsonDetailUrl;
    const boundariesUrl = container.dataset.boundariesUrl;
    const riversUrl = container.dataset.riversUrl;
    const citiesUrl = container.dataset.citiesUrl;
    const dataBaseUrl = container.dataset.dataUrl;
    const statusEl = container.querySelector('[data-landmark-map-status]');

    const map = L.map(container, {
      attributionControl: false,
      zoomControl: false,
      scrollWheelZoom: true,
      touchZoom: true,
      maxBounds: WORLD_BOUNDS,
      maxBoundsViscosity: 1,
      minZoom: 2,
      maxZoom: 14,
    });

    L.control
      .zoom({
        zoomInTitle: 'Zoom in',
        zoomOutTitle: 'Zoom out',
      })
      .addTo(map);

    map.fitBounds(WORLD_BOUNDS, { padding: [12, 12] });

    map.createPane('cityLabels');
    map.getPane('cityLabels').style.zIndex = 450;

    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 55,
      iconCreateFunction(cluster) {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<span class="landmark-map__cluster-count" aria-hidden="true">${count}</span>`,
          className: 'landmark-map__cluster',
          iconSize: [44, 44],
          iconAnchor: [22, 22],
        });
      },
    });

    map.addLayer(clusterGroup);

    let landOverviewLayer = null;
    let landDetailLayer = null;
    let boundaryLayer = null;
    let riverLayer = null;
    let detailDataLoaded = false;
    let detailDataLoading = null;
    let citiesData = null;
    let citiesLoading = null;
    const cityLabelLayer = L.layerGroup();

    function syncCityLabels() {
      const z = map.getZoom();
      const maxRank = maxCityRankForZoom(z);
      cityLabelLayer.clearLayers();

      if (!citiesData || maxRank < 0) {
        if (map.hasLayer(cityLabelLayer)) map.removeLayer(cityLabelLayer);
        return;
      }

      const bounds = map.getBounds().pad(0.12);
      citiesData.cities.forEach((city) => {
        if (city.rank > maxRank) return;
        if (!bounds.contains([city.lat, city.lng])) return;

        cityLabelLayer.addLayer(
          L.marker([city.lat, city.lng], {
            icon: createCityLabelIcon(city.name, z),
            pane: 'cityLabels',
            interactive: false,
            keyboard: false,
          })
        );
      });

      if (cityLabelLayer.getLayers().length && !map.hasLayer(cityLabelLayer)) {
        cityLabelLayer.addTo(map);
      }
      if (!cityLabelLayer.getLayers().length && map.hasLayer(cityLabelLayer)) {
        map.removeLayer(cityLabelLayer);
      }
    }

    function loadCities() {
      if (!citiesUrl) return Promise.resolve();
      if (citiesData) return Promise.resolve();
      if (citiesLoading) return citiesLoading;

      citiesLoading = fetchJson(citiesUrl).then((data) => {
        citiesData = data;
      });
      return citiesLoading;
    }

    function syncLandLayer() {
      const useDetail = map.getZoom() >= DETAIL_ZOOM && landDetailLayer;
      if (landOverviewLayer && map.hasLayer(landOverviewLayer) && useDetail) {
        map.removeLayer(landOverviewLayer);
      }
      if (landDetailLayer && useDetail && !map.hasLayer(landDetailLayer)) {
        landDetailLayer.addTo(map);
      }
      if (landOverviewLayer && !useDetail && !map.hasLayer(landOverviewLayer)) {
        landOverviewLayer.addTo(map);
      }
      if (landDetailLayer && !useDetail && map.hasLayer(landDetailLayer)) {
        map.removeLayer(landDetailLayer);
      }
      if (boundaryLayer) {
        boundaryLayer.setStyle(() => boundaryStyle(map));
      }
      if (riverLayer) {
        if (map.getZoom() >= RIVER_ZOOM) {
          if (!map.hasLayer(riverLayer)) riverLayer.addTo(map);
          riverLayer.setStyle(() => riverStyle(map));
        } else if (map.hasLayer(riverLayer)) {
          map.removeLayer(riverLayer);
        }
      }
    }

    function loadDetailLayers() {
      if (detailDataLoaded) return Promise.resolve();
      if (detailDataLoading) return detailDataLoading;

      detailDataLoading = Promise.all([
        fetchJson(landDetailUrl),
        fetchJson(riversUrl),
      ]).then(([landDetail, rivers]) => {
        landDetailLayer = L.geoJSON(landDetail, { style: landStyle });
        riverLayer = L.geoJSON(rivers, { style: () => riverStyle(map) });
        detailDataLoaded = true;
        syncLandLayer();
      });

      return detailDataLoading;
    }

    function syncMapView() {
      if (map.getZoom() >= DETAIL_ZOOM) {
        loadDetailLayers().catch(() => {});
      }
      if (map.getZoom() >= CITY_MIN_ZOOM) {
        loadCities()
          .then(() => syncCityLabels())
          .catch(() => {});
      } else {
        cityLabelLayer.clearLayers();
        if (map.hasLayer(cityLabelLayer)) map.removeLayer(cityLabelLayer);
      }
      syncLandLayer();
    }

    map.on('zoomend', syncMapView);
    map.on('moveend', syncMapView);

    Promise.all([
      fetchJson(landOverviewUrl),
      fetchJson(boundariesUrl),
      fetchAllMapProducts(dataBaseUrl),
    ])
      .then(([landOverview, boundaries, products]) => {
        landOverviewLayer = L.geoJSON(landOverview, { style: landStyle });
        landOverviewLayer.addTo(map);

        boundaryLayer = L.geoJSON(boundaries, {
          style: () => boundaryStyle(map),
          interactive: false,
        });
        boundaryLayer.addTo(map);

        products.forEach((product) => {
          const lat = parseFloat(product.lat);
          const lng = parseFloat(product.lng);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;

          const marker = L.marker([lat, lng], {
            icon: createPinIcon(product),
          });

          marker.bindTooltip(product.title, {
            direction: 'top',
            className: 'landmark-map__tooltip',
            offset: [0, -10],
          });

          clusterGroup.addLayer(marker);
        });

        container.dataset.landmarkPinCount = String(products.length);
        if (statusEl) {
          statusEl.textContent = '';
          statusEl.hidden = true;
        }

        syncMapView();
        map.invalidateSize();
      })
      .catch(() => {
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent = 'The map could not be loaded. Browse all landmarks instead.';
          statusEl.classList.add('landmark-map__status--error');
        }
      });
  }

  function boot() {
    if (typeof L === 'undefined' || typeof L.markerClusterGroup !== 'function') return;

    document.querySelectorAll('[data-landmark-map]').forEach((container) => {
      if (container.dataset.landmarkMapReady === 'true') return;
      container.dataset.landmarkMapReady = 'true';
      initMap(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
